'use client';

import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  TextField,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  WEEKDAY_DEFINITIONS,
  MAX_OPENING_SLOTS_PER_DAY,
  type OpeningHours,
  type WeekdayKey,
  type OpeningTimeSlot,
  emptyOpeningHours,
} from '../utils/providerPlaceOpeningHours';

type Props = {
  value: OpeningHours;
  onChange: (next: OpeningHours) => void;
  disabled?: boolean;
};

function updateDay(
  hours: OpeningHours,
  day: WeekdayKey,
  updater: (slots: OpeningTimeSlot[]) => OpeningTimeSlot[]
): OpeningHours {
  return {
    ...hours,
    [day]: updater(hours[day] ?? []),
  };
}

export default function OpeningHoursEditor({ value, onChange, disabled = false }: Props) {
  const safeValue = value ?? emptyOpeningHours();

  const handleAddSlot = (day: WeekdayKey) => {
    const slots = safeValue[day] ?? [];
    if (slots.length >= MAX_OPENING_SLOTS_PER_DAY) return;
    onChange(
      updateDay(safeValue, day, (current) => [...current, { open: '09:00', close: '18:00' }])
    );
  };

  const handleRemoveSlot = (day: WeekdayKey, index: number) => {
    onChange(
      updateDay(safeValue, day, (current) => current.filter((_, i) => i !== index))
    );
  };

  const handleSlotChange = (
    day: WeekdayKey,
    index: number,
    field: 'open' | 'close',
    nextValue: string
  ) => {
    onChange(
      updateDay(safeValue, day, (current) =>
        current.map((slot, i) => (i === index ? { ...slot, [field]: nextValue } : slot))
      )
    );
  };

  const handleCopyMonday = (day: WeekdayKey) => {
    onChange({
      ...safeValue,
      [day]: safeValue.monday.map((slot) => ({ ...slot })),
    });
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        Définis une ou plusieurs plages horaires par jour. Laisse vide pour indiquer « Fermé ». Si la fermeture est le lendemain (ex. 09:00 → 01:00), saisis une heure de fermeture antérieure à l&apos;ouverture.
      </Typography>
      {WEEKDAY_DEFINITIONS.map(({ key, label }, index) => {
        const slots = safeValue[key] ?? [];
        const isClosed = slots.length === 0;

        return (
          <Box key={key}>
            {index > 0 ? <Divider sx={{ mb: 1.5 }} /> : null}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'flex-start' }}
            >
              <Box sx={{ width: { xs: '100%', sm: 108 }, pt: { sm: 0.75 }, flexShrink: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {isClosed ? 'Fermé' : `${slots.length} plage${slots.length > 1 ? 's' : ''}`}
                </Typography>
              </Box>

              <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                {slots.map((slot, slotIndex) => (
                  <Stack
                    key={`${key}-${slotIndex}`}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <TextField
                      label="Ouverture"
                      type="time"
                      size="small"
                      value={slot.open}
                      disabled={disabled}
                      onChange={(e) => handleSlotChange(key, slotIndex, 'open', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 130 }}
                    />
                    <TextField
                      label="Fermeture"
                      type="time"
                      size="small"
                      value={slot.close}
                      disabled={disabled}
                      onChange={(e) => handleSlotChange(key, slotIndex, 'close', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 130 }}
                    />
                    <IconButton
                      size="small"
                      aria-label="Supprimer la plage"
                      disabled={disabled}
                      onClick={() => handleRemoveSlot(key, slotIndex)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    disabled={disabled || slots.length >= MAX_OPENING_SLOTS_PER_DAY}
                    onClick={() => handleAddSlot(key)}
                    sx={{ textTransform: 'none' }}
                  >
                    {isClosed ? 'Ajouter une plage' : 'Ajouter une plage'}
                  </Button>
                  {key !== 'monday' && safeValue.monday.length > 0 ? (
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<ContentCopyIcon />}
                      disabled={disabled}
                      onClick={() => handleCopyMonday(key)}
                      sx={{ textTransform: 'none' }}
                    >
                      Comme lundi
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}
